import React, { useCallback, useMemo } from "react";
import { useI18n } from "Hooks";
import { Badge } from "@toddle-edu/ds-web";
import _ from "lodash";

export const ALL_SCHOOLS_KEY = "ALL_SCHOOLS";

/**
 * Manages school selection logic for multi-select behaviour.
 * State (`selectedSchoolIds`) lives externally; this hook
 * receives it as a param and returns toggle handler + derived values.
 *
 * @param {Object[]} schools - Array of school objects
 * @param {null|Set<string>} selectedSchoolIds - null = all schools, Set = specific selection
 * @param {Function} setSelectedSchoolIds - State setter
 * @returns {{ onSchoolChange, isAllSchoolsSelected, selectedSchoolLabel }}
 */
const useSchoolSelection = (
  schools,
  selectedSchoolIds,
  setSelectedSchoolIds
) => {
  const { t } = useI18n(["common"]);

  const allSchoolIds = useMemo(() => _.map(schools, "id"), [schools]);

  const onSchoolChange = useCallback(
    schoolId => {
      if (schoolId === ALL_SCHOOLS_KEY) {
        // null means "all schools"; empty Set means "none selected"
        setSelectedSchoolIds(selectedSchoolIds === null ? new Set() : null);
      } else {
        // When null (all schools), expand to full Set first so we can deselect one
        const current =
          selectedSchoolIds === null
            ? new Set(allSchoolIds)
            : new Set(selectedSchoolIds);
        if (current.has(schoolId)) {
          current.delete(schoolId);
        } else {
          current.add(schoolId);
        }
        // If all schools end up selected, normalise back to null
        const allSelected =
          current.size === allSchoolIds.length &&
          allSchoolIds.every(id => current.has(id));
        setSelectedSchoolIds(allSelected ? null : current);
      }
    },
    [allSchoolIds, setSelectedSchoolIds, selectedSchoolIds]
  );

  const isAllSchoolsSelected =
    selectedSchoolIds === null && _.size(schools) > 0;

  const selectedSchoolLabel = useMemo(() => {
    if (selectedSchoolIds === null) return t("common:all");
    if (selectedSchoolIds.size === 0) return "";
    if (selectedSchoolIds.size === _.size(schools)) return t("common:all");
    if (selectedSchoolIds.size === 1) {
      const [singleId] = selectedSchoolIds;
      return _.get(_.find(schools, { id: singleId }), "label", "");
    }
    return (
      <Badge
        type="numeric"
        dsVersion="2.0"
        value={selectedSchoolIds.size}
        variant="dark"
        size="xxx-small"
      />
    );
  }, [selectedSchoolIds, schools, t]);

  return {
    onSchoolChange,
    isAllSchoolsSelected,
    selectedSchoolLabel,
    allSchoolIds,
  };
};

export default useSchoolSelection;
